/**
 * povCopy — the words that change by POV, kept in one place instead of typed
 * into the component that happens to render them first.
 *
 * ## Why this exists
 *
 * `SurfaceRosterRail`'s own doc comment already states the rule this file
 * enforces: *"An esports organisation's roster is its players; an agency's is
 * its talent; a community's is its members."* That rule went unapplied —
 * `DropHubSurface` hardcoded "Official creator" as the roster kicker for
 * every POV, so an esports Drop's roster card called its players creators, and
 * a community Drop's members the same. Same failure as the component that got
 * renamed away from `SurfaceCreatorRail` for exactly this reason, just not yet
 * fixed at the call site.
 *
 * `dropLayout()` collapses PUBLISHER, FRANCHISE, GAME, BRAND, COMMUNITY,
 * ESPORTS_PROFESSIONAL, ESPORTS_COLLEGIATE and AGENCY into one "PUBLISHER"
 * LAYOUT bucket — correctly, they all compose the same shell. But copy is not
 * layout, and those seven still need different words for the same roster. So
 * this keys off the raw POV, not the collapsed layout.
 *
 * ## The default is the safe fallback, not a guess
 *
 * A POV this has no entry for — a ninth value the enum grows into before this
 * file is updated — gets `DEFAULT_POV_COPY`, which is the least specific,
 * least wrong phrasing ("Creator" is at least true of everyone on a Drop's
 * roster). Never throws, never renders blank.
 */

export interface PovCopy {
    /** The roster card / rail kicker for one member in good standing. */
    rosterKicker: string;
    /** Same member, off this surface's title — BADGE filter mode. */
    rosterKickerOffTitle: string;
    /** The featured card's "+N more" trailer, e.g. "+3 official creators". */
    rosterFacesNoun: string;
    /** The active campaign window's countdown stat label — cadence wording. */
    windowStatLabel: string;
    /** The purchase-link action label, once the surface has shipped. */
    purchaseCta: string;
    /** The purchase-link action label AHEAD of release — "Pre-order" only means something for a title that ships. */
    preLaunchCta: string;
    /** The modes/what's-included strip's kicker. */
    modesKicker: string;
}

const DEFAULT_POV_COPY: PovCopy = {
    rosterKicker: "Creator",
    rosterKickerOffTitle: "Creator · off title",
    rosterFacesNoun: "creators",
    windowStatLabel: "This round",
    purchaseCta: "Learn more",
    preLaunchCta: "Learn more",
    modesKicker: "What's included",
};

const POV_COPY: Record<string, PovCopy> = {
    PUBLISHER: {
        rosterKicker: "Official creator",
        rosterKickerOffTitle: "Official creator · off title",
        rosterFacesNoun: "official creators",
        windowStatLabel: "This round",
        purchaseCta: "Get the game",
        preLaunchCta: "Pre-order",
        modesKicker: "What ships in the box",
    },
    FRANCHISE: {
        rosterKicker: "Official creator",
        rosterKickerOffTitle: "Official creator · off title",
        rosterFacesNoun: "official creators",
        windowStatLabel: "This round",
        purchaseCta: "Get the game",
        preLaunchCta: "Pre-order",
        modesKicker: "What ships in the box",
    },
    GAME: {
        rosterKicker: "Official creator",
        rosterKickerOffTitle: "Official creator · off title",
        rosterFacesNoun: "official creators",
        windowStatLabel: "This round",
        purchaseCta: "Get the game",
        preLaunchCta: "Pre-order",
        modesKicker: "What ships in the box",
    },
    BRAND: {
        rosterKicker: "Sponsored creator",
        rosterKickerOffTitle: "Sponsored creator · off title",
        rosterFacesNoun: "sponsored creators",
        windowStatLabel: "This week",
        purchaseCta: "Shop now",
        preLaunchCta: "Shop now",
        modesKicker: "What's included",
    },
    COMMUNITY: {
        rosterKicker: "Member",
        rosterKickerOffTitle: "Member · off title",
        rosterFacesNoun: "members",
        windowStatLabel: "This round",
        purchaseCta: "Learn more",
        preLaunchCta: "Learn more",
        modesKicker: "What's included",
    },
    ESPORTS_PROFESSIONAL: {
        rosterKicker: "Player",
        rosterKickerOffTitle: "Player · off title",
        rosterFacesNoun: "players",
        windowStatLabel: "This round",
        purchaseCta: "Get tickets",
        preLaunchCta: "Get tickets",
        modesKicker: "What's included",
    },
    ESPORTS_COLLEGIATE: {
        rosterKicker: "Player",
        rosterKickerOffTitle: "Player · off title",
        rosterFacesNoun: "players",
        windowStatLabel: "This round",
        purchaseCta: "Get tickets",
        preLaunchCta: "Get tickets",
        modesKicker: "What's included",
    },
    AGENCY: {
        rosterKicker: "Talent",
        rosterKickerOffTitle: "Talent · off title",
        rosterFacesNoun: "talent",
        windowStatLabel: "This round",
        purchaseCta: "Learn more",
        preLaunchCta: "Learn more",
        modesKicker: "What's included",
    },
};

/** `drop.pov`, falling back to the legacy `drop.dropType` signal — same fallback `dropLayout` uses. */
export function povCopy(pov: string | undefined, dropType: string | undefined): PovCopy {
    const who = pov || dropType;
    return who && POV_COPY[who] || DEFAULT_POV_COPY;
}
